import {
  useQuery,
  useMutation,
  useQueryClient,
  queryOptions,
} from '@tanstack/react-query'
import * as chronographService from '@/services/chronograph.service'
import type { SyncHistoryQuery } from '@/features/chronograph-dashboard/types'
import { isLiveJobState } from '@/components/custom/chronograph/shared'
import { getRefetchIntervalUnlessError } from '@/configs/query-client.config'
import { showSuccess } from '@/lib/toast'

// =============================================================================
// Query Keys Factory
// =============================================================================

export const chronographKeys = {
  all: ['chronograph'] as const,
  dashboard: (periodDays: number) =>
    [...chronographKeys.all, 'dashboard', periodDays] as const,
  status: () => [...chronographKeys.all, 'status'] as const,
  job: (id: string) => [...chronographKeys.all, 'job', id] as const,
  history: (query: SyncHistoryQuery) =>
    [...chronographKeys.all, 'history', query] as const,
  runDetail: (id: string) => [...chronographKeys.all, 'run', id] as const,
}

// =============================================================================
// Query Options
// =============================================================================

const DASHBOARD_STALE_TIME = 30_000
const STATUS_STALE_TIME = 10_000
const HISTORY_STALE_TIME = 15_000
const JOB_STATUS_STALE_TIME = 5_000

export const dashboardQueryOptions = (periodDays = 30) =>
  queryOptions({
    queryKey: chronographKeys.dashboard(periodDays),
    queryFn: () => chronographService.getDashboardStats(periodDays),
    staleTime: DASHBOARD_STALE_TIME,
    gcTime: 300_000,
  })

export const statusQueryOptions = () =>
  queryOptions({
    queryKey: chronographKeys.status(),
    queryFn: () => chronographService.getSyncStatus(),
    staleTime: STATUS_STALE_TIME,
    gcTime: 60_000,
    refetchInterval: (query) =>
      getRefetchIntervalUnlessError(query.state, 15_000),
  })

export const historyQueryOptions = (query: SyncHistoryQuery = {}) =>
  queryOptions({
    queryKey: chronographKeys.history(query),
    queryFn: () => chronographService.getSyncHistory(query),
    staleTime: HISTORY_STALE_TIME,
    gcTime: 300_000,
  })

export const jobStatusQueryOptions = (id: string | null) =>
  queryOptions({
    queryKey: chronographKeys.job(id ?? ''),
    queryFn: () => (id ? chronographService.getSyncJobStatus(id) : null),
    enabled: !!id,
    staleTime: JOB_STATUS_STALE_TIME,
    gcTime: 60_000,
    refetchInterval: (query) => {
      const job = query.state.data
      if (!job) return false
      return isLiveJobState(job.state)
        ? getRefetchIntervalUnlessError(query.state, JOB_STATUS_STALE_TIME)
        : false
    },
  })

export const runDetailQueryOptions = (id: string | null) =>
  queryOptions({
    queryKey: chronographKeys.runDetail(id ?? ''),
    queryFn: () => (id ? chronographService.getSyncRunDetail(id) : null),
    enabled: !!id,
    staleTime: 30_000,
  })

// =============================================================================
// Hooks
// =============================================================================

export function useDashboardStats(periodDays = 30) {
  return useQuery(dashboardQueryOptions(periodDays))
}

export function useSyncStatus() {
  return useQuery(statusQueryOptions())
}

export function useSyncHistory(query: SyncHistoryQuery = {}) {
  return useQuery(historyQueryOptions(query))
}

export function useSyncJobStatus(id: string | null) {
  return useQuery(jobStatusQueryOptions(id))
}

export function useSyncRunDetail(id: string | null) {
  return useQuery(runDetailQueryOptions(id))
}

export function useTriggerSyncMetrics() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: chronographService.triggerSyncMetrics,
    onSuccess: (data) => {
      showSuccess(data.message)
      queryClient.invalidateQueries({
        queryKey: chronographKeys.all,
      })
    },
  })
}

export function useTriggerSyncFull() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: chronographService.triggerSyncFull,
    onSuccess: (data) => {
      showSuccess(data.message)
      queryClient.invalidateQueries({
        queryKey: chronographKeys.all,
      })
    },
  })
}

export function useTriggerBackfill() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: chronographService.triggerBackfill,
    onSuccess: (data) => {
      showSuccess(data.message)
      queryClient.invalidateQueries({
        queryKey: chronographKeys.all,
      })
    },
  })
}
